import { Alert } from '@creditornot/cb-alert';
import { typographyCss, elevationCss, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { AxiosResponse } from 'axios';
import queryString from 'query-string';
import React, { useMemo } from 'react';
import jsonHighlight from 'react-syntax-highlighter/dist/cjs/languages/hljs/json';
import SyntaxHighlighter from 'react-syntax-highlighter/dist/cjs/light';
import githubGistSyntaxStyle from 'react-syntax-highlighter/dist/cjs/styles/hljs/github-gist';
import tomorrowNightSyntaxStyle from 'react-syntax-highlighter/dist/cjs/styles/hljs/tomorrow-night';
import styled from 'styled-components';

import { isPartialAxiosError, isPartialAxiosErrorResponse } from 'apis/axiosUtils';
import { useThemeMode } from 'common/themeMode';
import { getErrorContent } from 'common/utils';

SyntaxHighlighter.registerLanguage('json', jsonHighlight);

type Props = {
  /** Additional error information shown below the title */
  children?: React.ReactNode;
  error: any;
  className?: string;
  style?: React.CSSProperties;
  initialOpen?: boolean;
};

const getErrorResponseURL = (response: AxiosResponse) => {
  const { method, url, params } = response.config;

  if (!url) {
    return undefined;
  }

  return `${method ? method.toUpperCase() : ''} ${url}${!!params && Object.keys(params).length ? '?' : ''}${
    params ? queryString.stringify(params) : ''
  }`;
};

const getErrorResponseTitle = (response: AxiosResponse) => {
  return [response.status, response.statusText].filter(Boolean).join(' ');
};

const Root = styled(Alert).attrs({ variant: 'error', size: 'medium' })`
  max-width: 100%;

  .ReactCollapse--collapse {
    transition: height 400ms cubic-bezier(0.19, 1, 0.22, 1); /* easeOutExpo */
  }
`;

const Content = styled.div``;

const ShowMoreContent = styled.div`
  padding-block-start: 12px;
  min-height: 0;
  min-width: 0;
`;

const FooterURL = styled.div`
  margin-block-start: 8px;
  ${typographyCss.Small}
  color: ${cbSemanticCssVars.textSecondary};
  font-family: Monaco, monospace;
  word-break: break-word;
`;

const ErrorDisplay = styled.div`
  border-radius: 8px;
  ${typographyCss.Small}
  color: ${cbSemanticCssVars.textPrimary};
  max-height: 230px;
  max-width: 100%;
  overflow: auto;
  ${elevationCss(2)}

  pre {
    margin: 0;
  }
`;

const ErrorContent = styled.div``;

const NoResponseContent = styled.div`
  padding-block-start: 8px;
  padding-block-end: 8px;
  text-align: center;
  color: ${cbSemanticCssVars.textSecondary};
`;

const StyledSyntaxHighlighter = styled(SyntaxHighlighter)`
  border-radius: 3px;
`;

type ErrorNotificationProps = {
  children?: React.ReactNode;
  className?: string;
  jsonData?: any;
  style?: React.CSSProperties;
  title: string;
  url?: string;
  initialOpen?: boolean;
};

const ErrorNotification: React.FC<ErrorNotificationProps> = ({
  children,
  jsonData,
  url,
  title,
  initialOpen = false,
  ...rest
}) => {
  const { themeMode } = useThemeMode();

  return (
    <Root
      {...rest}
      title={title}
      showMoreContent={
        <ShowMoreContent>
          <ErrorDisplay>
            {jsonData ? (
              <StyledSyntaxHighlighter
                language="json"
                style={themeMode === 'light' ? githubGistSyntaxStyle : tomorrowNightSyntaxStyle}
              >
                {JSON.stringify(jsonData, null, 2)}
              </StyledSyntaxHighlighter>
            ) : (
              <NoResponseContent>(empty content)</NoResponseContent>
            )}
          </ErrorDisplay>

          {!!url && <FooterURL>{url}</FooterURL>}
        </ShowMoreContent>
      }
    >
      <Content>{!!children && <ErrorContent>{children}</ErrorContent>}</Content>
    </Root>
  );
};

const APIErrorNotification: React.FC<Props> = ({ children, error, style, ...rest }) => {
  const url: string | undefined = useMemo(() => {
    if (isPartialAxiosError(error) && error.response) {
      return getErrorResponseURL(error.response);
    }

    if (isPartialAxiosErrorResponse(error)) {
      return getErrorResponseURL(error);
    }

    return undefined;
  }, [error]);

  const errorTitle: string | undefined = useMemo(() => {
    if (!error) {
      return 'Unknown error';
    }

    if (isPartialAxiosError(error) && error.response) {
      return getErrorResponseTitle(error.response);
    }

    if (isPartialAxiosErrorResponse(error)) {
      return getErrorResponseTitle(error);
    }

    if (error instanceof Error && error.message) {
      return 'Error';
    }

    if (typeof error === 'string') {
      return error;
    }

    return undefined;
  }, [error]);

  const errorContent = useMemo(() => getErrorContent(error), [error]);

  const jsonData: object | undefined = useMemo(() => {
    if (isPartialAxiosError(error) && error.response) {
      return error.response.data;
    }

    if (isPartialAxiosErrorResponse(error)) {
      return error.data;
    }

    if (error instanceof Error) {
      return error.message || undefined;
    }

    if (typeof error === 'string') {
      return undefined;
    }

    if (typeof error === 'object') {
      return error;
    }

    return undefined;
  }, [error]);

  return errorTitle ? (
    <ErrorNotification {...rest} jsonData={jsonData} style={style} title={errorTitle} url={url}>
      {children || errorContent}
    </ErrorNotification>
  ) : null;
};

export default APIErrorNotification;
